import { useEffect } from "react";

// Inputs that raise no keyboard.
const NO_KEYBOARD = new Set(["checkbox", "radio", "button", "submit", "file", "range", "color"]);

const editing = (): boolean => {
  const el = document.activeElement;
  return (
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLInputElement && !NO_KEYBOARD.has(el.type)) ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
};

/** Publishes the visible area as `--viewport-top` and `--viewport-height` while a text
 * field has focus on a touch screen, so a panel placed with them stays above the
 * keyboard however Safari pans: iOS shrinks only the visual viewport for its keyboard.
 * Unset otherwise, so a pinch-zoom, which shrinks the visual viewport too, does not
 * shrink the panel. */
export const useVisibleViewport = (): void => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    const touch = window.matchMedia("(pointer: coarse)");
    const update = () => {
      if (editing() && touch.matches) {
        root.style.setProperty("--viewport-top", `${Math.round(viewport.offsetTop)}px`);
        root.style.setProperty("--viewport-height", `${Math.round(viewport.height)}px`);
      } else {
        root.style.removeProperty("--viewport-top");
        root.style.removeProperty("--viewport-height");
      }
    };
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      root.style.removeProperty("--viewport-top");
      root.style.removeProperty("--viewport-height");
    };
  }, []);
};
